import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { IoSearchOutline, IoChevronDown, IoCheckmark } from "react-icons/io5";
import { FiX } from "react-icons/fi";
import Button from "./Button";

const SearchSelect = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select an option...",
  searchable = true,
  disabled = false,
  multiple = false,
  isClearable = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 220; // max-h-48 (~192px) + search row
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    setDropdownStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 99999,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  useEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e) => {
      // Scrolling the options list itself must not close the dropdown
      if (dropdownRef.current?.contains(e.target)) return;
      setIsOpen(false);
      setSearchTerm("");
    };
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        triggerRef.current && !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Filter options — match against label + subLabel so description text is searchable too
  const filteredOptions = options.filter((option) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      option.label?.toLowerCase().includes(term) ||
      option.subLabel?.toLowerCase().includes(term)
    );
  });

  const handleSelect = (option) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.some(
        (item) => item.value === option.value,
      );
      let newValues;
      if (isSelected) {
        newValues = currentValues.filter((item) => item.value !== option.value);
      } else {
        newValues = [...currentValues, option];
      }
      onChange?.(newValues);
    } else {
      onChange?.(option);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const handleRemoveItem = (optionToRemove, e) => {
    e.stopPropagation();
    if (multiple && Array.isArray(value)) {
      const newValues = value.filter(
        (item) => item.value !== optionToRemove.value,
      );
      onChange?.(newValues);
    }
  };

  const getDisplayValue = () => {
    if (multiple) return placeholder;
    const selectedOption =
      options.find((opt) => opt.value === value?.value) || value;
    return selectedOption?.label || placeholder;
  };

  const isOptionSelected = (option) => {
    if (multiple && Array.isArray(value)) {
      return value.some((item) => item.value === option.value);
    }
    const selectedOption =
      options.find((opt) => opt.value === value?.value) || value;
    return selectedOption?.value === option.value;
  };

  const hasValue = () => {
    if (multiple) return Array.isArray(value) && value.length > 0;
    return value !== null && value !== undefined;
  };

  return (
    <div className="relative w-full">
      <button
        ref={triggerRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between border border-mediumgray rounded px-3 py-3 leading-4 text-sm focus:outline-none
          ${disabled ? "bg-lightgray text-darkgray" : "bg-white hover:border-mediumgray"}
          ${className}
        `}
        type="button"
      >
        <span
          className={`text-left line-clamp-1 ${
            !value ||
            (multiple && (!Array.isArray(value) || value.length === 0))
              ? "text-darkgray"
              : "text-black"
          }`}
        >
          {getDisplayValue()}
        </span>

        <div className="flex items-center gap-2">
          {isClearable && hasValue() && !disabled && (
            <button onClick={handleClear} type="button">
              <FiX className="w-3.5 h-3.5 text-darkgray" />
            </button>
          )}
          <IoChevronDown
            className={`h-4 w-4 text-darkgray transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white border border-mediumgray rounded shadow-lg"
          style={dropdownStyle}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {searchable && (
            <div className="px-3 py-2 border-b border-mediumgray">
              <div className="flex items-center gap-2">
                <IoSearchOutline className="text-darkgray" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full outline-none text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          <ul className="m-0 max-h-48 overflow-auto">
            {filteredOptions.length === 0 ? (
              <li className="px-4 py-2 text-center text-darkgray">
                No options found
              </li>
            ) : (
              filteredOptions.map((option, index) => (
                <li key={option.value || index} className="m-0">
                  <button
                    onClick={() => handleSelect(option)}
                    aria-pressed={multiple ? isOptionSelected(option) : undefined}
                    className={`
                      w-full px-4 py-2.5 text-left hover:bg-lightgray focus:outline-none
                      ${isOptionSelected(option) && !multiple ? "bg-mediumgray" : ""}
                    `}
                  >
                    <div className={multiple ? "flex items-start gap-2" : ""}>
                      {multiple && (
                        <span
                          className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                            isOptionSelected(option)
                              ? "bg-black border-black"
                              : "border-mediumgray"
                          }`}
                        >
                          {isOptionSelected(option) && (
                            <IoCheckmark className="text-white" size={12} />
                          )}
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm">{option.label}</p>
                        {option.subLabel && (
                          <p className="text-[10px] sm:text-xs text-darkergray mt-0.5 leading-snug">
                            {option.subLabel}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body
      )}

      {multiple && Array.isArray(value) && value.length > 0 && (
        <div className="mt-2 space-y-1">
          {value.map((selectedItem, index) => (
            <div
              key={selectedItem.value || index}
              className="flex items-center justify-between p-3 bg-lightgray text-black text-sm rounded"
            >
              <span>{selectedItem.label}</span>
              <Button
                onClick={(e) => handleRemoveItem(selectedItem, e)}
                className="rounded p-1 transition-colors ml-2"
              >
                <FiX size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
